import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    AccountSummaryStatusWrapper,
    AccountSummaryWrapper,
    DepositButton,
    FreeBetsTextWithHelper,
    HelpIconWrapper,
    NameStyle,
    RowRecordStyle,
    UserNameStyle,
    UserRecordStyle,
    ValueStyle,
} from './AccountSummary.style';
import { Loader } from 'src/domains/layouts/webview/components/loader/Loader';
import { LogoutButton } from 'src/domains/players/webview/components/Account/summaryTab/accountSummary/logoutButton/LogoutButton';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { AccountSummaryState } from './AccountSummary.state';
import { Amount } from 'src_common/common/amount/Amount';
import { Tooltip } from './Tooltip/Tooltip';
import { AccountSummaryBonuses } from './AccountSummarryBonuses/AccountSummaryBonuses';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { useCommon } from 'src/domains/common/Common';
import { UsersState } from 'src/domains/players/state/UsersState';
import { AccountState } from 'src/domains/players/shared/Types';

const AccountSummaryBalance = observer('AccountSummaryBalance', () => {
    const common = useCommon();
    const usersState = UsersState.get(common);
    const configComponents = ConfigComponents.get(common);
    const starRouter = StarRouter.get(common);
    const basicData = BasicDataModel.get(common);

    const playableBalance =
        usersState.walletData.valueReady?.playableBalance === undefined
            ? undefined
            : new Amount(usersState.walletData.valueReady.playableBalance);
    const freeBetsAmount = configComponents.precision.newFromAnything(
        usersState.freeBetsData.valueReady?.response?.totalAmount ?? 0
    );
    const withdrawableBalance = new Amount(usersState.walletData.valueReady?.withdrawableBalance ?? '0');
    const backofficeWithdrawableBalance = new Amount(
        usersState.walletData.valueReady?.backofficeWithdrawableBalance ?? '0'
    );
    const failedWithdrawalsFunds = backofficeWithdrawableBalance.sub(withdrawableBalance);

    return (
        <>
            <RowRecordStyle>
                <NameStyle data-test='credit-balance-name'>
                    <I18n
                        langKey='account.summary.credit-balance'
                        defaultText='Credit balance'
                    />
                </NameStyle>
                <ValueStyle data-test='credit-balance-value'>{basicData.money(playableBalance)}</ValueStyle>
            </RowRecordStyle>

            {failedWithdrawalsFunds.isGreaterThanZero() ? (
                <RowRecordStyle>
                    <FreeBetsTextWithHelper>
                        <I18n
                            langKey='account.summary.failed-withdrawal-funds'
                            defaultText='Failed withdrawal funds '
                        />
                        <Tooltip
                            type='error'
                            content={
                                <I18n
                                    langKey='account.summary.failed-withdrawal-funds.text'
                                    defaultText="We processed your withdrawal, but the bank returned the funds. Please provide a copy of your card showing your BACS information to enable us to pay your withdrawal. If you've already provided this, we will process your withdrawal shortly."
                                />
                            }
                        />
                    </FreeBetsTextWithHelper>
                    <ValueStyle>{basicData.money(failedWithdrawalsFunds)}</ValueStyle>
                </RowRecordStyle>
            ) : null}

            <RowRecordStyle>
                <FreeBetsTextWithHelper>
                    <I18n
                        langKey='account.summary.free-bet-credits'
                        defaultText='Free Bet Credits '
                    />
                    <HelpIconWrapper onClick={starRouter.redirectToFreeBetCreditsInfo} />
                </FreeBetsTextWithHelper>
                <ValueStyle>{basicData.money(freeBetsAmount)}</ValueStyle>
            </RowRecordStyle>
        </>
    );
});

const AccountSummaryReady = observer('AccountSummaryReady', () => {
    const common = useCommon();
    const configComponents = ConfigComponents.get(common);
    const { config } = configComponents;
    const accountSummaryState = AccountSummaryState.get(common);
    const usersState = UsersState.get(common);

    const id = usersState.basicData.valueReady?.id ?? -1;
    const name = usersState.basicData.valueReady?.name;
    const idPrefix = config.accountIdPrefix;

    return (
        <AccountSummaryWrapper>
            <UserRecordStyle>
                <UserNameStyle>{name}</UserNameStyle>
                <LogoutButton />
            </UserRecordStyle>
            <RowRecordStyle>
                <NameStyle data-test='account-number-name'>
                    <I18n
                        langKey='account.summary.account-number'
                        defaultText='Account number'
                    />
                </NameStyle>
                <ValueStyle data-test='account-number-value'>{`${idPrefix}${id}`}</ValueStyle>
            </RowRecordStyle>

            <AccountSummaryBalance />

            <RowRecordStyle>
                <NameStyle>
                    <I18n
                        langKey='account.summary.login-time'
                        defaultText='Logged in time'
                    />
                </NameStyle>
                <ValueStyle>{accountSummaryState.loggedCounter}</ValueStyle>
            </RowRecordStyle>
            <RowRecordStyle>
                <DepositButton
                    size='large'
                    onClick={accountSummaryState.redirectToTopUp}
                    dataTest='deposit-button'
                >
                    <I18n
                        langKey='account.summary.deposit'
                        defaultText='Deposit'
                    />
                </DepositButton>
            </RowRecordStyle>
        </AccountSummaryWrapper>
    );
});

export const AccountSummaryNew = observer('AccountSummaryNew', () => {
    const common = useCommon();
    const { bonusEngine } = ConfigComponents.get(common).config;
    const usersState = UsersState.get(common);
    const account = AccountState.get(common).account;

    if (account !== null) {
        const basicData = usersState.basicData.get();
        const walletData = usersState.walletData.get();

        if (basicData.type === 'error' || walletData.type === 'error') {
            return (
                <AccountSummaryStatusWrapper>
                    <div>Something went wrong...</div>
                    <div onClick={account.resetAll}>Try again</div>
                </AccountSummaryStatusWrapper>
            );
        }

        if (basicData.type === 'loading' || walletData.type === 'loading') {
            return (
                <AccountSummaryStatusWrapper>
                    <Loader version='dark' />
                </AccountSummaryStatusWrapper>
            );
        }

        if (bonusEngine) {
            return <AccountSummaryBonuses />;
        }

        return <AccountSummaryReady />;
    }
    return null;
});
